import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { Plus, Search, X, Phone, Mail, Calendar, Users, AlertTriangle, Clock, Eye, ChevronDown, ChevronRight, Download, Upload, FileText, Trash2 } from 'lucide-react';

const fmtCurrency = (v) => {
  if (!v) return '0';
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  return Number(v).toLocaleString('en-IN');
};

const STAGE_COLORS = {
  'Enquiry': { bg: '#E6F1FB', color: '#3B5BA9' },
  'Quote Sent': { bg: '#FEF3C7', color: '#BA7517' },
  'Confirmed': { bg: '#ECFDF5', color: '#2D8B5E' },
  'Checked In': { bg: '#D1FAE5', color: '#065F46' },
  'Checked Out': { bg: '#F3F4F6', color: '#6B7280' },
  'Cancelled': { bg: '#FEF2F2', color: '#D4726A' },
  'No Show': { bg: '#FEF2F2', color: '#991B1B' },
  'Lost': { bg: '#F3F4F6', color: '#9CA3AF' },
};

const VIEWS = ['Pipeline', 'Table', 'Calendar', 'Occupancy'];

export default function BookingPipeline() {
  const [bookings, setBookings] = useState([]);
  const [config, setConfig] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('Pipeline');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [occupancy, setOccupancy] = useState(null);

  const refresh = () => {
    api.get('/bookings').then(r => setBookings(r.data.bookings)).catch(() => {});
    api.get('/bookings/alerts').then(r => setAlerts(r.data.alerts)).catch(() => {});
    api.get('/bookings/stats').then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get('/bookings/config').then(r => setConfig(r.data)).catch(() => {});
    api.get('/bookings/occupancy').then(r => setOccupancy(r.data)).catch(() => {});
    refresh();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return bookings;
    const q = search.toLowerCase();
    return bookings.filter(b =>
      (b.guest_name || '').toLowerCase().includes(q) ||
      (b.phone || '').includes(q) ||
      (b.source || '').toLowerCase().includes(q)
    );
  }, [bookings, search]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const importRef = useRef(null);
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post('/bookings/import-csv', fd); alert(`Imported ${r.data.imported} bookings`); refresh(); } catch {}
    ev.target.value = '';
  };

  if (!config) return <p className="text-xs py-8 text-center" style={{ color: '#9CA3AF' }}>Loading...</p>;

  return (
    <div className="max-w-7xl mx-auto space-y-4 pb-24" data-testid="booking-pipeline">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#1B2A4A' }}>Stays By MindEscapes — Bookings</h1>
          <p className="text-xs" style={{ color: '#6B7280' }}>Track enquiries, bookings, occupancy & follow-ups</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={`${API_URL}/api/bookings/csv-template?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="bookings-template-btn">
            <FileText size={11} /> Template
          </a>
          <a href={`${API_URL}/api/bookings/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="bookings-export-btn">
            <Download size={11} /> Export
          </a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1.5 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="bookings-import-btn">
            <Upload size={11} /> Import
          </button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }} data-testid="new-booking-btn">
            <Plus size={14} /> New Booking
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Total</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Confirmed</p>
            <p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{stats.confirmed}</p>
          </div>
          <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Conversion</p>
            <p className="text-lg font-bold" style={{ color: '#2A7B88' }}>{stats.conversion_rate}%</p>
          </div>
          {occupancy?.summary && (
            <>
              <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
                <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Avg Occupancy</p>
                <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{occupancy.summary.avg_occupancy_pct}%</p>
              </div>
              <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
                <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>RevPAR</p>
                <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{fmtCurrency(occupancy.summary.revpar)}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#D4726A', borderWidth: '1.5px' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: '#D4726A' }} />
            <span className="text-xs font-semibold" style={{ color: '#D4726A' }}>Follow-Up Alerts ({alerts.length})</span>
          </div>
          <div className="space-y-1">
            {alerts.slice(0, 5).map((a, i) => (
              <p key={i} className="text-xs" style={{ color: a.severity === 'critical' ? '#D4726A' : a.severity === 'warning' ? '#BA7517' : '#6B7280' }}>
                {a.severity === 'critical' ? '!!' : a.severity === 'warning' ? '!' : '-'} {a.message}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* View tabs + search */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {VIEWS.map(v => (
            <button key={v} onClick={() => setView(v)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ backgroundColor: view === v ? '#1B2A4A' : '#fff', color: view === v ? '#fff' : '#6B7280', border: `1px solid ${view === v ? '#1B2A4A' : '#E5E1DC'}` }}
              data-testid={`view-${v.toLowerCase()}`}>{v}</button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-2" style={{ color: '#9CA3AF' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guest..."
            className="text-xs border rounded pl-7 pr-2 py-1.5 w-48" style={{ borderColor: '#E5E1DC' }} />
        </div>
      </div>

      {/* Pipeline (Kanban) View */}
      {view === 'Pipeline' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-view">
          {['Enquiry', 'Quote Sent', 'Confirmed', 'Checked In', 'Checked Out'].map(stage => {
            const stageBookings = filtered.filter(b => b.stage === stage);
            const sc = STAGE_COLORS[stage] || {};
            return (
              <div key={stage} className="min-w-[240px] flex-shrink-0">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-xs font-semibold" style={{ color: sc.color }}>{stage}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: sc.bg, color: sc.color }}>{stageBookings.length}</span>
                </div>
                <div className="space-y-2">
                  {stageBookings.map(b => (
                    <div key={b.id} onClick={() => setSelected(b)}
                      className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow"
                      style={{ borderColor: '#E5E1DC', borderLeft: `3px solid ${sc.color}` }}>
                      <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{b.guest_name}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px]" style={{ color: '#6B7280' }}>
                        <span>{b.room_type} x{b.num_rooms}</span>
                        <span>{b.num_nights}N</span>
                        <span className="font-medium" style={{ color: '#2A7B88' }}>{fmtCurrency(b.total_value)}</span>
                      </div>
                      <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>{b.check_in_date} — {b.check_out_date}</p>
                      {b.assigned_to && <p className="text-[10px]" style={{ color: '#6B7280' }}>{b.assigned_to}</p>}
                      <p className="text-[9px] mt-0.5 px-1.5 py-0.5 rounded inline-block" style={{ backgroundColor: '#F7F5F2', color: '#9CA3AF' }}>{b.source}</p>
                    </div>
                  ))}
                  {stageBookings.length === 0 && (
                    <div className="rounded-lg border border-dashed p-4 text-center" style={{ borderColor: '#E5E1DC' }}>
                      <p className="text-[10px]" style={{ color: '#9CA3AF' }}>No bookings</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {view === 'Table' && (
        <div className="bg-white rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }} data-testid="table-view">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: '#F7F5F2' }}>
                {['Guest', 'Room', 'Check-in', 'Check-out', 'Nights', 'Value', 'Source', 'Stage', 'Assigned'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {filtered.map(b => {
                const sc = STAGE_COLORS[b.stage] || {};
                return (
                  <tr key={b.id} onClick={() => setSelected(b)} className="cursor-pointer hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{b.guest_name}</td>
                    <td className="px-3 py-2">{b.room_type} x{b.num_rooms}</td>
                    <td className="px-3 py-2">{b.check_in_date}</td>
                    <td className="px-3 py-2">{b.check_out_date}</td>
                    <td className="px-3 py-2">{b.num_nights}</td>
                    <td className="px-3 py-2 font-medium" style={{ color: '#2A7B88' }}>{fmtCurrency(b.total_value)}</td>
                    <td className="px-3 py-2">{b.source}</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{b.stage}</span></td>
                    <td className="px-3 py-2">{b.assigned_to}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center" style={{ color: '#9CA3AF' }}>No bookings yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Calendar View */}
      {view === 'Calendar' && occupancy && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="calendar-view">
          <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>Room Occupancy Calendar</h3>
          <div className="grid grid-cols-7 gap-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} className="text-center text-[10px] font-medium py-1" style={{ color: '#6B7280' }}>{d}</div>
            ))}
            {occupancy.days.map(d => {
              const dt = new Date(d.date);
              const dayOfWeek = dt.getDay();
              const color = d.occupancy_pct >= 80 ? '#2D8B5E' : d.occupancy_pct >= 40 ? '#BA7517' : d.occupancy_pct > 0 ? '#E6F1FB' : '#F9FAFB';
              const textColor = d.occupancy_pct >= 80 ? '#fff' : d.occupancy_pct >= 40 ? '#fff' : '#1B2A4A';
              return (
                <div key={d.date} className="rounded p-1.5 text-center" style={{ backgroundColor: color, gridColumnStart: dayOfWeek === 0 ? 7 : dayOfWeek }}>
                  <p className="text-[10px] font-medium" style={{ color: textColor }}>{dt.getDate()}</p>
                  <p className="text-[8px]" style={{ color: textColor, opacity: 0.8 }}>{d.rooms_occupied}/{d.total_rooms}</p>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-[10px]" style={{ color: '#6B7280' }}>
            <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#2D8B5E' }} />80%+ Full</span>
            <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#BA7517' }} />40-80%</span>
            <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#E6F1FB' }} />1+ rooms</span>
            <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E1DC' }} />Empty</span>
          </div>
        </div>
      )}

      {/* Occupancy View */}
      {view === 'Occupancy' && occupancy && (
        <div className="space-y-4" data-testid="occupancy-view">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Avg Occupancy</p>
              <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{occupancy.summary.avg_occupancy_pct}%</p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>{occupancy.summary.total_room_nights_sold} room-nights sold</p>
            </div>
            <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>ADR</p>
              <p className="text-xl font-bold" style={{ color: '#2A7B88' }}>{fmtCurrency(occupancy.summary.adr)}</p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>Average Daily Rate</p>
            </div>
            <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>RevPAR</p>
              <p className="text-xl font-bold" style={{ color: '#2A7B88' }}>{fmtCurrency(occupancy.summary.revpar)}</p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>Revenue Per Available Room</p>
            </div>
            <div className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Total Revenue</p>
              <p className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{fmtCurrency(occupancy.summary.total_room_revenue)}</p>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>{occupancy.total_rooms} rooms ({occupancy.room_config.Suite}S + {occupancy.room_config.Deluxe}D)</p>
            </div>
          </div>
        </div>
      )}

      {/* New Booking Form Modal */}
      {showForm && <BookingForm config={config} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}

      {/* Booking Detail Panel */}
      {selected && <BookingDetail booking={selected} config={config} onClose={() => setSelected(null)} onUpdated={() => { setSelected(null); refresh(); }} />}
    </div>
  );
}

function BookingForm({ config, onClose, onSaved }) {
  const [form, setForm] = useState({
    guest_name: '', phone: '', email: '', source: 'Phone call', room_type: 'Suite',
    num_rooms: 1, check_in_date: '', check_out_date: '', adults: 2, children: 0,
    rate_quoted: '', special_requests: '', assigned_to: 'Siddharth', follow_up_date: '',
  });
  const [saving, setSaving] = useState(false);

  const nights = form.check_in_date && form.check_out_date
    ? Math.max(0, Math.ceil((new Date(form.check_out_date) - new Date(form.check_in_date)) / 86400000)) : 0;
  const total = (parseInt(form.num_rooms) || 1) * nights * (parseFloat(form.rate_quoted) || 0);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/bookings', form);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-start justify-center pt-16 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>New Booking / Enquiry</h3>
          <button onClick={onClose}><X size={18} style={{ color: '#6B7280' }} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Guest Name *</label>
            <input value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} data-testid="guest-name" /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Email</label>
            <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Source</label>
            <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }}>
              {config.sources.map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Room Type</label>
            <select value={form.room_type} onChange={e => setForm({ ...form, room_type: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }}>
              {config.room_types.map(r => <option key={r}>{r}</option>)}</select></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Rooms</label>
            <input type="number" value={form.num_rooms} onChange={e => setForm({ ...form, num_rooms: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Check-in</label>
            <input type="date" value={form.check_in_date} onChange={e => setForm({ ...form, check_in_date: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} data-testid="check-in" /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Check-out</label>
            <input type="date" value={form.check_out_date} onChange={e => setForm({ ...form, check_out_date: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} data-testid="check-out" /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Adults</label>
            <input type="number" value={form.adults} onChange={e => setForm({ ...form, adults: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Children</label>
            <input type="number" value={form.children} onChange={e => setForm({ ...form, children: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Rate/night (INR)</label>
            <input type="number" value={form.rate_quoted} onChange={e => setForm({ ...form, rate_quoted: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Assigned To</label>
            <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }}>
              {config.assignees.map(a => <option key={a}>{a}</option>)}</select></div>
          <div><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Follow-up Date</label>
            <input type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
          <div className="col-span-2"><label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Special Requests</label>
            <textarea value={form.special_requests} onChange={e => setForm({ ...form, special_requests: e.target.value })}
              className="w-full text-xs border rounded px-2 py-1.5 h-16" style={{ borderColor: '#E5E1DC' }} /></div>
        </div>
        {nights > 0 && (
          <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}>
            <p className="text-xs"><strong>{nights} nights</strong> x {form.num_rooms} room(s) x {fmtCurrency(parseFloat(form.rate_quoted) || 0)}/night = <strong style={{ color: '#2A7B88' }}>{fmtCurrency(total)}</strong></p>
          </div>
        )}
        <button onClick={submit} disabled={saving || !form.guest_name}
          className="mt-4 px-6 py-2 rounded-md text-sm font-medium text-white disabled:opacity-40"
          style={{ backgroundColor: '#1B2A4A' }} data-testid="save-booking">
          {saving ? 'Saving...' : 'Create Booking'}
        </button>
      </div>
    </div>
  );
}

function BookingDetail({ booking, config, onClose, onUpdated }) {
  const [note, setNote] = useState('');
  const [newStage, setNewStage] = useState(booking.stage);
  const sc = STAGE_COLORS[booking.stage] || {};

  const updateStage = async (stage) => {
    await api.put(`/bookings/${booking.id}`, { stage, update_note: `Stage: ${booking.stage} → ${stage}` });
    onUpdated();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/bookings/${booking.id}/note`, { note });
    setNote('');
    onUpdated();
  };

  const deleteBooking = async () => {
    if (!window.confirm(`Delete booking for "${booking.guest_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${booking.id}`);
      onUpdated();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || 'Unknown error'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-start justify-end z-50" onClick={onClose}>
      <div className="bg-white h-full w-full max-w-md shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center justify-between">
            <span className="px-2 py-0.5 rounded text-[11px] font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{booking.stage}</span>
            <button onClick={onClose}><X size={18} style={{ color: '#6B7280' }} /></button>
          </div>
          <h3 className="text-base font-bold mt-2" style={{ color: '#1B2A4A' }}>{booking.guest_name}</h3>
          <div className="flex gap-3 mt-1 text-xs" style={{ color: '#6B7280' }}>
            {booking.phone && <span className="flex items-center gap-1"><Phone size={11} />{booking.phone}</span>}
            {booking.email && <span className="flex items-center gap-1"><Mail size={11} />{booking.email}</span>}
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span style={{ color: '#9CA3AF' }}>Room:</span> <strong>{booking.room_type} x{booking.num_rooms}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Nights:</span> <strong>{booking.num_nights}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Check-in:</span> <strong>{booking.check_in_date}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Check-out:</span> <strong>{booking.check_out_date}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Rate:</span> <strong>{fmtCurrency(booking.rate_quoted)}/night</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Total:</span> <strong style={{ color: '#2A7B88' }}>{fmtCurrency(booking.total_value)}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Source:</span> <strong>{booking.source}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Assigned:</span> <strong>{booking.assigned_to}</strong></div>
            <div><span style={{ color: '#9CA3AF' }}>Guests:</span> <strong>{booking.adults}A + {booking.children}C</strong></div>
            {booking.follow_up_date && <div><span style={{ color: '#9CA3AF' }}>Follow-up:</span> <strong>{booking.follow_up_date}</strong></div>}
          </div>
          {booking.special_requests && <p className="text-xs p-2 rounded" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>{booking.special_requests}</p>}

          {/* Stage change */}
          <div>
            <p className="text-[10px] font-medium mb-1" style={{ color: '#6B7280' }}>Change Stage</p>
            <div className="flex flex-wrap gap-1">
              {config.stages.map(s => (
                <button key={s} onClick={() => updateStage(s)} disabled={s === booking.stage}
                  className="px-2 py-1 rounded text-[10px] font-medium disabled:opacity-30"
                  style={{ backgroundColor: STAGE_COLORS[s]?.bg || '#F3F4F6', color: STAGE_COLORS[s]?.color || '#6B7280' }}>{s}</button>
              ))}
            </div>
          </div>

          {/* Add note */}
          <div>
            <p className="text-[10px] font-medium mb-1" style={{ color: '#6B7280' }}>Add Follow-up Note</p>
            <div className="flex gap-1">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Called, sent rates..."
                className="flex-1 text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} data-testid="booking-note-input" />
              <button onClick={addNote} className="px-3 py-1.5 rounded text-xs font-medium text-white"
                style={{ backgroundColor: '#2A7B88' }}>Add</button>
            </div>
          </div>

          {/* Update log */}
          <div>
            <p className="text-[10px] font-medium mb-1" style={{ color: '#6B7280' }}>Activity Log</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {(booking.updates || []).slice().reverse().map((u, i) => (
                <div key={i} className="p-2 rounded text-[10px]" style={{ backgroundColor: '#F7F5F2' }}>
                  <p style={{ color: '#1B2A4A' }}>{u.note}</p>
                  <p style={{ color: '#9CA3AF' }}>{u.by} · {new Date(u.at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Danger zone */}
          <div className="pt-3 border-t" style={{ borderColor: '#FEE2E2' }}>
            <button onClick={deleteBooking} className="flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-medium border" style={{ borderColor: '#FEE2E2', color: '#D4726A' }} data-testid="delete-booking-btn">
              <Trash2 size={12} /> Delete Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
